import React from "react";
import {
    TableViewStore,
    BaseListViewStore,
    BaasicDropdownStore,
} from "core/stores";
import applicationContext from "core/utils/applicationContext";
import { resolveColumns } from "application/facility/util/resolveColumns";
import { TeamsService } from "application/teams/services";
import { observable, action } from "mobx";
import { CoreUserService } from "application/user/services";

@applicationContext
class TeamsViewStore extends BaseListViewStore {
    @observable accessor counter = 0;
    constructor(rootStore) {
        const teamsService = new TeamsService(
            rootStore.application.baasic.apiClient
        );
        const coreUserService = new CoreUserService(
            rootStore.application.baasic.apiClient
        );
        super(rootStore, {
            name: "teams",
            autoInit: true,
            authorization: {
                create: "portalSection.create",
                delete: "portalSection.delete",
            },
            routes: {
                create: () =>
                    this.rootStore.routerStore.goTo(
                        "master.app.main.teams.create"
                    ),
                edit: (id) => {
                    this.setChildNavigationTitle(
                        (i) => i.id === id,
                        (r) => r.name
                    );
                    return this.rootStore.routerStore.goTo(
                        "master.app.main.teams.edit",
                        { id: id }
                    );
                },
            },
            actions: () => {
                return {
                    find: async (params) => {
                        params.embed = "TeamUsers";

                        const sortParam =
                            params.orderBy && params.orderDirection
                                ? `${params.orderBy}|${params.orderDirection}`
                                : "name|asc";

                        const response = await teamsService.find({
                            ...params,
                            sort: sortParam,
                            rpp: 25,
                        });
                        return response.data;
                    },
                    delete: async (item) => {
                        await teamsService.delete(item);
                    },
                };
            },
        });

        const userRoles = this.rootStore.userStore.user.roles;
        this.isAdmin = userRoles.includes("Administrators");

        this.queryUtility.onResetFilter = () => {
            this.userStore.setValue(null);
            this.userStore.filterTerm = "";
        };

        this.userStore = new BaasicDropdownStore(
            {
                filterable: true,
                textField: "name",
            },
            {
                fetchFunc: async (term) => {
                    let params = {
                        searchQuery: term,
                        sort: "firstName|asc",
                        rpp: 50,
                        embed: "CoreMembership",
                    };

                    const response = await coreUserService.find(params);

                    return response.data.item
                        .filter((user) => user.coreMembership.isApproved)
                        .map((u) => ({
                            id: u.id,
                            name: u.firstName + " " + u.lastName,
                        }));
                },
            }
        );

        this.setTableStore(
            new TableViewStore(this.queryUtility, {
                columns: resolveColumns([
                    {
                        key: "name",
                        title: "TEAMS.LIST.COLUMNS.NAME",
                        width: 500,
                    },
                    {
                        key: "userIds",
                        title: "TEAMS.LIST.COLUMNS.USERS",
                        sortable: false,
                        cell: (item) => {
                            const users = item.dataItem.teamUsers || [];
                            return (
                                <td>
                                    {users
                                        .map((u) => u.firstName + " " + u.lastName)
                                        .filter(Boolean)
                                        .join(", ")}
                                </td>
                            );
                        },
                    },
                ]),
                actions: this.isAdmin
                    ? {
                          onEdit: async (item) => this.routes.edit(item.id),
                          onDelete: async (item) => this.onDelete(item),
                      }
                    : {},
            })
        );
    }

    @action.bound
    filterOnChange() {
        this.userStore.filterTerm = "";
    }

    @action.bound
    async onDelete(item) {
        this.rootStore.modalStore.showConfirm(
            "TEAMS.LIST.COLUMNS.DELETE_MESSAGE",
            async () => {
                try {
                    await this.actions.delete(item);
                    this.rootStore.notificationStore.success(
                        "Team successfully deleted."
                    );
                    this.queryUtility.fetch();
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        "An error ocurred while trying to delete a team."
                    );
                }
            }
        );
    }

    @action.bound
    notifySuccessDelete() {
        this.rootStore.notificationStore.success("TEAMS.DELETE.SUCCESS");
    }
}

export default TeamsViewStore;
